import React, { useMemo, useState } from "react";
import { Palette, Sparkles, Library, FolderOpen, Wand2, Check } from "lucide-react";
import { t } from "../i18n";
import { track } from "../services/analytics.js";
import "./DecorLanding.css";

function Field({ label, type = "text", value, onChange, placeholder, autoComplete }) {
    return (
        <label className="decor-field">
            <div className="decor-label">{label}</div>
            <input
                className="decor-input"
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                autoComplete={autoComplete}
            />
        </label>
    );
}

function FeatureCard({ icon: Icon, title, description, gradient }) {
    return (
        <div className="decor-feature-card" style={{ '--gradient': gradient }}>
            <div className="decor-feature-icon">
                <Icon size={32} />
            </div>
            <h3 className="decor-feature-title">{title}</h3>
            <p className="decor-feature-desc">{description}</p>
        </div>
    );
}

function PricingCard({ title, price, credits, features, highlighted }) {
    return (
        <div className={`decor-pricing-card ${highlighted ? 'highlighted' : ''}`}>
            {highlighted && <div className="decor-pricing-badge">{t("decor.pricing.popular")}</div>}
            <h3 className="decor-pricing-title">{title}</h3>
            <div className="decor-pricing-price">
                {price}
                {price !== t("decor.pricing.free") && <span className="decor-pricing-period">/mois</span>}
            </div>
            <div className="decor-pricing-credits">{credits}</div>
            <ul className="decor-pricing-features">
                {features.map((feature, i) => (
                    <li key={i}>
                        <Check size={16} />
                        <span>{feature}</span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export default function DecorLanding() {
    const [tab, setTab] = useState("login");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState(null);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState("");
    const [resetStatus, setResetStatus] = useState("idle");
    const [resetMsg, setResetMsg] = useState("");

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [name, setName] = useState("");

    const canSubmit = useMemo(() => {
        if (!email) return false;
        if (tab === "signup") return Boolean(name && password);
        return Boolean(password);
    }, [email, password, name, tab]);

    async function handlePasswordAuth(e) {
        e.preventDefault();
        setMsg(null);
        setLoading(true);
        try {
            const url = tab === "login" ? "/api/auth/login" : "/api/auth/signup";
            const body = tab === "login" ? { email, password } : { name, email, password };

            const res = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(body)
            });

            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || "Erreur inconnue");

            if (tab === "signup" && data.needsEmailConfirmation) {
                setMsg({ type: "info", text: t("decor.auth.emailConfirm") });
                return;
            }

            track(tab === "signup" ? "signup_success" : "login_success", {
                method: "password",
                product: "decor"
            });
            window.location.href = "/projects";
        } catch (err) {
            setMsg({ type: "error", text: err.message || "Erreur" });
        } finally {
            setLoading(false);
        }
    }

    async function handleResetPassword(e) {
        e.preventDefault();
        setResetStatus("loading");
        setResetMsg("");
        try {
            const res = await fetch("/api/auth/forgot", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    email: resetEmail,
                    redirectTo: window.location.origin + "/reset-password"
                })
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || "Erreur");
            setResetStatus("success");
            setResetMsg(t("decor.forgot.sent"));
        } catch (err) {
            setResetStatus("error");
            setResetMsg(err.message || "Erreur");
        }
    }

    return (
        <div className="decor-page">
            {/* Hero Section */}
            <header className="decor-hero">
                <div className="decor-hero-bg"></div>
                <div className="decor-hero-content">
                    <div className="decor-hero-left">
                        <div className="decor-brand">
                            <Wand2 size={40} className="decor-brand-icon" />
                            <div>
                                <h1 className="decor-brand-name">{t("decor.brand.name")}</h1>
                                <p className="decor-brand-tag">{t("decor.brand.tag")}</p>
                            </div>
                        </div>

                        <h2 className="decor-hero-title">{t("decor.hero.title")}</h2>
                        <p className="decor-hero-lead">{t("decor.hero.lead")}</p>

                        <div className="decor-hero-stats">
                            <div className="decor-stat">
                                <div className="decor-stat-value">✨ IA</div>
                                <div className="decor-stat-label">{t("decor.stat.ai")}</div>
                            </div>
                            <div className="decor-stat">
                                <div className="decor-stat-value">4x</div>
                                <div className="decor-stat-label">{t("decor.stat.variations")}</div>
                            </div>
                            <div className="decor-stat">
                                <div className="decor-stat-value">∞</div>
                                <div className="decor-stat-label">{t("decor.stat.versions")}</div>
                            </div>
                        </div>
                    </div>

                    <div className="decor-hero-right">
                        <div className="decor-auth-card">
                            <div className="decor-tabs">
                                <button
                                    className={tab === "login" ? "decor-tab active" : "decor-tab"}
                                    onClick={() => setTab("login")}
                                >
                                    {t("decor.tabs.login")}
                                </button>
                                <button
                                    className={tab === "signup" ? "decor-tab active" : "decor-tab"}
                                    onClick={() => setTab("signup")}
                                >
                                    {t("decor.tabs.signup")}
                                </button>
                            </div>

                            <form onSubmit={handlePasswordAuth} className="decor-form">
                                {tab === "signup" && (
                                    <Field
                                        label={t("decor.field.name")}
                                        value={name}
                                        onChange={setName}
                                        placeholder={t("decor.placeholder.name")}
                                        autoComplete="name"
                                    />
                                )}

                                <Field
                                    label={t("decor.field.email")}
                                    type="email"
                                    value={email}
                                    onChange={setEmail}
                                    placeholder={t("decor.placeholder.email")}
                                    autoComplete="email"
                                />

                                <Field
                                    label={t("decor.field.password")}
                                    type="password"
                                    value={password}
                                    onChange={setPassword}
                                    placeholder={t("decor.placeholder.password")}
                                    autoComplete={tab === "login" ? "current-password" : "new-password"}
                                />

                                {tab === "login" && (
                                    <button
                                        type="button"
                                        className="decor-forgot-link"
                                        onClick={() => {
                                            setResetEmail(email);
                                            setResetStatus("idle");
                                            setResetMsg("");
                                            setResetOpen(true);
                                        }}
                                    >
                                        {t("decor.forgot.link")}
                                    </button>
                                )}

                                {msg?.type === "error" && <div className="decor-alert error">{msg.text}</div>}
                                {msg?.type === "info" && <div className="decor-alert info">{msg.text}</div>}

                                <button className="decor-btn-primary" disabled={!canSubmit || loading}>
                                    {loading ? "..." : tab === "login" ? t("decor.submit.login") : t("decor.submit.signup")}
                                </button>

                                <div className="decor-terms">
                                    {t("terms.prefix")}{" "}
                                    <a href="/legal#cgu">{t("terms.cgu")}</a>{" "}
                                    {t("terms.and")}{" "}
                                    <a href="/legal#privacy">{t("terms.privacy")}</a>.
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </header>

            {/* Features Section */}
            <section className="decor-features">
                <div className="decor-section-header">
                    <h2 className="decor-section-title">{t("decor.features.title")}</h2>
                    <p className="decor-section-subtitle">{t("decor.features.subtitle")}</p>
                </div>

                <div className="decor-features-grid">
                    <FeatureCard
                        icon={Wand2}
                        title={t("decor.feature.editor.title")}
                        description={t("decor.feature.editor.desc")}
                        gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                    />
                    <FeatureCard
                        icon={Library}
                        title={t("decor.feature.libraries.title")}
                        description={t("decor.feature.libraries.desc")}
                        gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                    />
                    <FeatureCard
                        icon={Sparkles}
                        title={t("decor.feature.ai.title")}
                        description={t("decor.feature.ai.desc")}
                        gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)"
                    />
                    <FeatureCard
                        icon={FolderOpen}
                        title={t("decor.feature.projects.title")}
                        description={t("decor.feature.projects.desc")}
                        gradient="linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)"
                    />
                </div>
            </section>

            {/* Pricing Section */}
            <section className="decor-pricing">
                <div className="decor-section-header">
                    <h2 className="decor-section-title">{t("decor.pricing.title")}</h2>
                    <p className="decor-section-subtitle">{t("decor.pricing.subtitle")}</p>
                </div>

                <div className="decor-pricing-grid">
                    <PricingCard
                        title={t("decor.pricing.starter.title")}
                        price={t("decor.pricing.free")}
                        credits={t("decor.pricing.starter.credits")}
                        features={[
                            t("decor.pricing.starter.feat1"),
                            t("decor.pricing.starter.feat2"),
                            t("decor.pricing.starter.feat3")
                        ]}
                    />
                    <PricingCard
                        title={t("decor.pricing.pack50.title")}
                        price="9.99€"
                        credits={t("decor.pricing.pack50.credits")}
                        features={[
                            t("decor.pricing.pack50.feat1"),
                            t("decor.pricing.pack50.feat2"),
                            t("decor.pricing.pack50.feat3")
                        ]}
                        highlighted
                    />
                    <PricingCard
                        title={t("decor.pricing.pack200.title")}
                        price="29.99€"
                        credits={t("decor.pricing.pack200.credits")}
                        features={[
                            t("decor.pricing.pack200.feat1"),
                            t("decor.pricing.pack200.feat2"),
                            t("decor.pricing.pack200.feat3")
                        ]}
                    />
                    <PricingCard
                        title={t("decor.pricing.pro.title")}
                        price="49.99€"
                        credits={t("decor.pricing.pro.credits")}
                        features={[
                            t("decor.pricing.pro.feat1"),
                            t("decor.pricing.pro.feat2"),
                            t("decor.pricing.pro.feat3"),
                            t("decor.pricing.pro.feat4")
                        ]}
                    />
                </div>
            </section>

            {/* FAQ Section */}
            <section className="decor-faq">
                <div className="decor-section-header">
                    <h2 className="decor-section-title">{t("decor.faq.title")}</h2>
                </div>

                <div className="decor-faq-list">
                    <details className="decor-faq-item">
                        <summary>{t("decor.faq.q1")}</summary>
                        <p>{t("decor.faq.a1")}</p>
                    </details>
                    <details className="decor-faq-item">
                        <summary>{t("decor.faq.q2")}</summary>
                        <p>{t("decor.faq.a2")}</p>
                    </details>
                    <details className="decor-faq-item">
                        <summary>{t("decor.faq.q3")}</summary>
                        <p>{t("decor.faq.a3")}</p>
                    </details>
                    <details className="decor-faq-item">
                        <summary>{t("decor.faq.q4")}</summary>
                        <p>{t("decor.faq.a4")}</p>
                    </details>
                </div>
            </section>

            {/* Footer */}
            <footer className="decor-footer">
                <div className="decor-footer-content">
                    <div className="decor-footer-brand">
                        <Wand2 size={24} />
                        <span>{t("decor.brand.name")}</span>
                    </div>
                    <div className="decor-footer-copy">
                        © {new Date().getFullYear()} {t("decor.brand.name")}. {t("decor.footer.rights")}
                    </div>
                    <div className="decor-footer-links">
                        <a href="/legal#cgu">{t("terms.cgu")}</a>
                        <a href="/legal#privacy">{t("terms.privacy")}</a>
                    </div>
                </div>
            </footer>

            {resetOpen && (
                <div className="decor-auth-modal-overlay" role="dialog" aria-modal="true">
                    <div className="decor-auth-modal">
                        <div className="decor-auth-modal-header">
                            <h3>{t("decor.forgot.title")}</h3>
                            <button
                                type="button"
                                className="decor-auth-modal-close"
                                onClick={() => setResetOpen(false)}
                            >
                                ×
                            </button>
                        </div>
                        <p className="decor-auth-modal-subtitle">{t("decor.forgot.subtitle")}</p>
                        <form onSubmit={handleResetPassword} className="decor-auth-modal-form">
                            <Field
                                label={t("decor.forgot.email")}
                                type="email"
                                value={resetEmail}
                                onChange={setResetEmail}
                                placeholder={t("decor.placeholder.email")}
                                autoComplete="email"
                            />
                            {resetMsg && (
                                <div className={`decor-alert ${resetStatus === "success" ? "info" : "error"}`}>
                                    {resetMsg}
                                </div>
                            )}
                            <button className="decor-btn-primary" disabled={!resetEmail || resetStatus === "loading"}>
                                {resetStatus === "loading" ? "..." : t("decor.forgot.submit")}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
